import { useMemo, useState, useEffect } from "react";
import {
  Box, Card, CardContent, Stack, Typography, IconButton, alpha,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import { useCycleFilter } from "../../cycleFilterContext";

function formatUsd(n: number, compact = false) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 2,
  }).format(n);
}

function formatDate(iso: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

interface Insight {
  key: string;
  emoji: string;
  headline: string;
  body: string;
  color: string;
}

/**
 * InsightsRotator — auto-rotating "did you know" card for the dashboard.
 *
 * Pulls live aggregates from `dashboard.getDailyInsights` and converts
 * each fact into a short, human-readable insight. Card auto-advances
 * every 7 seconds; arrows let the user step through manually. The
 * rotation pauses when the user interacts with the controls.
 */
export function InsightsRotator() {
  const { cycleArg } = useCycleFilter();
  const insights = useStableQuery(api.dashboard.getDailyInsights, cycleArg, null);
  const topVendors = useStableQuery(api.dashboard.getTopVendors, { ...cycleArg, limit: 3 }, []);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const items: Insight[] = useMemo(() => {
    if (!insights) return [];
    const list: Insight[] = [];

    if (insights.biggest) {
      list.push({
        key: "biggest",
        emoji: "🏔️",
        headline: "Biggest single transaction",
        body: `${insights.biggest.vendorName ?? insights.biggest.description.slice(0, 60)} — ${formatUsd(Math.abs(insights.biggest.amount))} on ${formatDate(insights.biggest.date)}.`,
        color: "#fd79a8",
      });
    }

    if (insights.topCategory) {
      list.push({
        key: "topCategory",
        emoji: "🏷️",
        headline: "Most-used category",
        body: `"${insights.topCategory.category}" — ${insights.topCategory.count} transactions categorized this cycle.`,
        color: "#74b9ff",
      });
    }

    if (insights.autoFlowCount > 0) {
      const pct =
        insights.cycleTotal > 0
          ? Math.round((insights.autoFlowCount / insights.cycleTotal) * 100)
          : 0;
      list.push({
        key: "autoFlow",
        emoji: "✨",
        headline: "Auto-flow saved time",
        body: `${insights.autoFlowCount} rows (${pct}% of cycle) posted without invoice matching, per Michelle's per-account rules.`,
        color: "#a29bfe",
      });
    }

    if (topVendors && topVendors.length > 0) {
      const top = topVendors[0];
      list.push({
        key: "topVendor",
        emoji: "🏆",
        headline: "Top vendor by volume",
        body: `${top.vendorName} accounts for ${formatUsd(top.amount)} across ${top.count} transactions in this cycle.`,
        color: "#ffeaa7",
      });
    }

    if (insights.reconciliationRate >= 0) {
      const r = insights.reconciliationRate;
      const tone = r >= 90 ? "🟢" : r >= 70 ? "🟡" : "🔴";
      list.push({
        key: "rate",
        emoji: tone,
        headline: "Reconciliation rate",
        body: `${r}% of this cycle is either matched to an invoice or covered by an auto-flow account.`,
        color: r >= 90 ? "#55efc4" : r >= 70 ? "#fdcb6e" : "#ff7675",
      });
    }

    if (insights.importedCount > 0 && insights.cycleTotal > 0) {
      const remaining = insights.cycleTotal - insights.importedCount;
      list.push({
        key: "import",
        emoji: "📤",
        headline: "Posted to QuickBooks",
        body: `${insights.importedCount} of ${insights.cycleTotal} cycle transactions are in QBD. ${remaining} still in flight.`,
        color: "#6c5ce7",
      });
    }

    return list;
  }, [insights, topVendors]);

  // Auto-rotate
  useEffect(() => {
    if (paused || items.length === 0) return;
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, 7000);
    return () => window.clearInterval(id);
  }, [items.length, paused]);

  useEffect(() => {
    if (idx >= items.length) setIdx(0);
  }, [idx, items.length]);

  if (items.length === 0) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <EmojiObjectsOutlinedIcon sx={{ color: "var(--c-link-accent)" }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Insights
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Daily highlights will appear here once this cycle has transactions to analyse.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const current = items[idx];

  return (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${alpha(current.color, 0.12)}, ${alpha(current.color, 0.02)})`,
        border: `1px solid ${alpha(current.color, 0.25)}`,
        transition: "background 600ms ease, border-color 600ms ease",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EmojiObjectsOutlinedIcon sx={{ color: current.color }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Insights
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              size="small"
              aria-label="Previous insight"
              onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 32, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
              {idx + 1}/{items.length}
            </Typography>
            <IconButton
              size="small"
              aria-label="Next insight"
              onClick={() => setIdx((i) => (i + 1) % items.length)}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Typography sx={{ fontSize: 28, lineHeight: 1, flexShrink: 0, mt: 0.25 }} aria-hidden>
            {current.emoji}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: current.color, mb: 0.5 }}
            >
              {current.headline}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
              {current.body}
            </Typography>
          </Box>
        </Stack>

        {/* Dot pager */}
        <Stack direction="row" spacing={0.75} sx={{ mt: 2 }}>
          {items.map((it, i) => (
            <Box
              key={it.key}
              role="button"
              tabIndex={0}
              onClick={() => setIdx(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIdx(i);
                }
              }}
              aria-label={`Show insight ${i + 1}: ${it.headline}`}
              sx={{
                width: i === idx ? 24 : 8,
                height: 6,
                borderRadius: 3,
                backgroundColor:
                  i === idx ? current.color : alpha(current.color, 0.25),
                cursor: "pointer",
                transition: "width 300ms ease, background 300ms ease",
              }}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
